
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Info } from 'lucide-react';
import { SETTING_INFO } from '@/data/settingInfo';

interface InfoPopupProps {
  content?: string | React.ReactNode;
  settingKey?: string;
  disabled?: boolean;
  disabledMessage?: string;
  modes?: string[];
  darkMode?: boolean;
}

export const InfoPopup: React.FC<InfoPopupProps> = ({ content, settingKey, disabled, disabledMessage, modes, darkMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0, arrowLeft: 50, isBelow: false });

  // Automatically fetch info from SETTING_INFO if settingKey is provided
  const info = settingKey ? SETTING_INFO[settingKey] : null;
  const finalContent = content || info?.description || '';
  const finalModes = modes || info?.modes;

  const updatePosition = () => {
    if (!buttonRef.current) return;
    
    const rect = buttonRef.current.getBoundingClientRect();
    
    // Calculate position relative to viewport
    let centerX = rect.left + rect.width / 2;
    let left = centerX;
    // Align better with the i icon - show popup above by default
    let top = rect.top - 12; 

    // Screen bounds check
    const popupWidth = 280; 
    const margin = 12;

    if (left - popupWidth / 2 < margin) {
      left = popupWidth / 2 + margin;
    } else if (left + popupWidth / 2 > window.innerWidth - margin) {
      left = window.innerWidth - popupWidth / 2 - margin;
    }

    // Top boundary check - if too close to top, show below
    const estimatedHeight = 160; 
    let isBelow = false;
    if (top - estimatedHeight < margin) {
      top = rect.bottom + 12;
      isBelow = true;
    }

    // Calculate arrow position relative to popup center
    const arrowLeft = 50 + ((centerX - left) / popupWidth) * 100;

    setPopupPos({ top, left, arrowLeft, isBelow });
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      // Update position on scroll and resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      const interval = setInterval(updatePosition, 100); // Polling for items that move with animations
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
        clearInterval(interval);
      };
    }
  }, [isOpen]);

  const handleMouseEnter = () => {
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    setIsOpen(false);
  };

  const popupContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: popupPos.isBelow ? -5 : 5, x: '-50%' }}
          animate={{ opacity: 1, scale: 1, y: 0, x: '-50%' }}
          exit={{ opacity: 0, scale: 0.95, y: popupPos.isBelow ? -5 : 5, x: '-50%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 400 }}
          style={{
            position: 'fixed',
            top: popupPos.top,
            left: popupPos.left,
            zIndex: 10000,
            transformOrigin: popupPos.isBelow ? 'top center' : 'bottom center'
          }}
          className={`w-[280px] p-5 rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.3)] border backdrop-blur-xl pointer-events-none ${
            darkMode 
              ? 'bg-neutral-900/95 border-white/10 text-white' 
              : 'bg-white/95 border-neutral-200 text-neutral-800'
          }`}
        >
          {disabled && disabledMessage ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 mb-1">
                  <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400">
                    <i className="fa-solid fa-circle-exclamation text-[10px]" />
                  </div>
                  <span className="text-cyan-400 font-black uppercase tracking-[0.2em] text-[10px]">Disabled</span>
              </div>
              <p className="opacity-80 leading-relaxed text-[11px] font-medium">{disabledMessage}</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2 mb-1 opacity-40">
                  <i className="fa-solid fa-circle-info text-[10px]" />
                  <span className="font-black uppercase tracking-[0.2em] text-[10px]">Information</span>
              </div>
              <p className="leading-relaxed font-bold text-[11px]">{finalContent}</p>
              {finalModes && finalModes.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2 pt-3 border-t border-white/5">
                  {finalModes.map(mode => (
                    <span key={mode} className="px-2 py-1 rounded-lg bg-blue-500/20 text-blue-400 text-[9px] font-black uppercase tracking-tighter">
                      {mode}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Arrow */}
          <div 
            style={{ 
              left: `${popupPos.arrowLeft}%`,
              top: popupPos.isBelow ? '0' : '100%',
              transform: popupPos.isBelow ? 'translateX(-50%) translateY(-100%) rotate(180deg)' : 'translateX(-50%)'
            }}
            className={`absolute border-8 border-transparent ${
              darkMode ? 'border-t-neutral-900/95' : 'border-t-white/95'
            }`} 
          />
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className="inline-flex items-center flex-shrink-0 ml-1.5" ref={containerRef}>
      <button
        ref={buttonRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`w-6 h-6 rounded-full flex items-center justify-center transition-all pointer-events-auto group ${
          isOpen 
            ? 'bg-cyan-500 text-white shadow-[0_0_15px_rgba(6,182,212,0.4)] scale-110' 
            : 'hover:bg-cyan-500/10'
        }`}
      >
        <Info className={`w-3.5 h-3.5 transition-colors ${
          isOpen ? 'text-white' : 'text-neutral-500 group-hover:text-cyan-500'
        }`} />
      </button>

      {typeof document !== 'undefined' && createPortal(popupContent, document.body)}
    </div>
  );
};
